import { http, httpForm } from './client';
import type {
  ActionKind,
  BookCardsResponse,
  BookHistoryItem,
  CardGateway,
  Comment,
  ConnectFriendResponse,
  FeedResponse,
  FriendItem,
  FriendShelfItem,
  HistoryItem,
  NotificationPrefs,
  PaymentMethod,
  ReferralsResponse,
  ShelfItem,
  ShelfVisibility,
  StreakDay,
  SubscriptionPaymentResponse,
  SubscriptionPlanInfo,
  Topic,
  TransactionItem,
  User,
  UserSearchItem,
} from './types';

/**
 * Все API-методы одного приложения. Каждый метод явно типизирован под
 * envelope-ответ бэкенда.
 */

export const api = {
  auth: {
    telegram: (init_data: string) =>
      http.post<{ token: string; user: User; is_new_user: boolean }>(
        '/api/v1/auth/telegram',
        { init_data },
        { skipAuth: true },
      ),
    logout: () => http.post<{ ok: boolean }>('/api/v1/auth/logout'),
  },

  feed: {
    list: (limit = 10) => http.get<FeedResponse['data']>(`/api/v1/feed?limit=${limit}`),
    seen: (card_id: string) =>
      http.post<{ cards_seen: number; limit: number; daily_limit_reached: boolean; counted: boolean }>(
        '/api/v1/feed/seen',
        { card_id },
      ),
  },

  cards: {
    toggle: (id: string, action: ActionKind) =>
      http.post<{ card_id: string; action: ActionKind; active: boolean; stats: { likes: number; dislikes: number; bookmarks: number; shares: number } }>(
        `/api/v1/cards/${id}/${action}`,
      ),
    setBookmarkVisibility: (id: string, hidden_from_public: boolean) =>
      http.patch<{ ok: boolean }>(`/api/v1/cards/${id}/bookmark/visibility`, { hidden_from_public }),
    removeBookmark: (id: string) => http.delete<{ ok: boolean }>(`/api/v1/cards/${id}/bookmark`),
    sharePrep: (id: string) =>
      http.post<{ prepared_message_id: string; expiration_date: number }>(
        `/api/v1/cards/${id}/share-prep`,
      ),
  },

  books: {
    cards: (id: string) => http.get<BookCardsResponse['data']>(`/api/v1/books/${id}/cards`),
  },

  shelf: {
    me: (params: { topic?: string; book?: string; hidden_only?: boolean; page?: number } = {}) => {
      const q = new URLSearchParams();
      if (params.topic) q.set('topic', params.topic);
      if (params.book) q.set('book', params.book);
      if (params.hidden_only) q.set('hidden_only', '1');
      if (params.page) q.set('page', String(params.page));
      const s = q.toString();
      return http.get<ShelfItem[]>(`/api/v1/me/shelf${s ? `?${s}` : ''}`);
    },
    user: (userId: number, page = 1) => http.get<ShelfItem[]>(`/api/v1/users/${userId}/shelf?page=${page}`),
    friends: (page = 1) =>
      http.get<FriendShelfItem[]>(`/api/v1/me/friends/shelf?page=${page}`),
  },

  topics: {
    list: () => http.get<Topic[]>('/api/v1/topics'),
  },

  me: {
    show: () => http.get<User>('/api/v1/me'),
    setTopics: (topics: string[]) => http.patch<{ topics: string[] }>('/api/v1/me/topics', { topics }),
    setLang: (lang: string) => http.patch<{ lang: string }>('/api/v1/me/lang', { lang }),
    setShelfVisibility: (shelf_visibility: ShelfVisibility) =>
      http.patch<{ shelf_visibility: ShelfVisibility }>('/api/v1/me/shelf-visibility', { shelf_visibility }),
    stats: () =>
      http.get<{ cards_read_total: number; bookmarks_total: number; streak_days: number; books_opened: number }>(
        '/api/v1/me/stats',
      ),
    transactions: (page = 1, type?: 'subscription' | 'referral_payout' | 'withdrawal' | 'deposit') => {
      const q = new URLSearchParams({ page: String(page) });
      if (type) q.set('type', type);
      return http.get<TransactionItem[]>(`/api/v1/me/transactions?${q.toString()}`);
    },
    referrals: () => http.get<ReferralsResponse>('/api/v1/me/referrals'),
    history: (page = 1) => http.get<HistoryItem[]>(`/api/v1/me/history?page=${page}`),
    booksHistory: (page = 1) => http.get<BookHistoryItem[]>(`/api/v1/me/books?page=${page}`),
    streakCalendar: (days = 30) => http.get<StreakDay[]>(`/api/v1/me/streak-calendar?days=${days}`),
    setSwipeHintSeen: () => http.patch<{ swipe_hint_seen: boolean }>('/api/v1/me/swipe-hint-seen', {}),
    setAnalyticsOptOut: (analytics_opt_out: boolean) =>
      http.patch<{ analytics_opt_out: boolean }>('/api/v1/me/analytics-opt-out', { analytics_opt_out }),
    notificationPrefs: {
      get: () => http.get<NotificationPrefs>('/api/v1/me/notification-prefs'),
      patch: (prefs: Partial<NotificationPrefs>) =>
        http.patch<NotificationPrefs>('/api/v1/me/notification-prefs', { prefs }),
    },
  },

  users: {
    search: (q: string) => http.get<UserSearchItem[]>(`/api/v1/users/search?q=${encodeURIComponent(q)}`),
  },

  friends: {
    list: (page = 1) =>
      http.get<FriendItem[]>(`/api/v1/me/friends?status=accepted&page=${page}`),
    connect: (ref_code: string) =>
      http.post<ConnectFriendResponse>('/api/v1/friends/connect', { ref_code }),
    add: (user_id: number) =>
      http.post<{ friendship_id: number; status: 'pending' | 'accepted'; user: { id: number; first_name: string | null; username: string | null; photo_url: string | null } }>(
        '/api/v1/friends/add',
        { user_id },
      ),
    accept: (id: number) =>
      http.post<{ friendship_id: number; status: 'accepted' }>(`/api/v1/friends/${id}/accept`),
    remove: (id: number) =>
      http.delete<{ ok: boolean }>(`/api/v1/friends/${id}`),
  },

  comments: {
    list: (cardId: string, sort: 'top' | 'new' = 'top', page = 1) =>
      http.get<Comment[]>(`/api/v1/cards/${cardId}/comments?sort=${sort}&page=${page}`),
    add: (cardId: string, text: string, parent_id?: number) =>
      http.post<{ id: number; text: string; created_at: string }>(
        `/api/v1/cards/${cardId}/comments`,
        { text, parent_id: parent_id ?? null },
      ),
    delete: (id: number) => http.delete<{ ok: boolean }>(`/api/v1/comments/${id}`),
    like: (id: number) => http.post<{ id: number; likes_count: number }>(`/api/v1/comments/${id}/like`),
  },

  subscription: {
    plans: () => http.get<SubscriptionPlanInfo[]>('/api/v1/subscription/plans'),
    buy: (plan: 'week' | 'month' | 'year') =>
      http.post<SubscriptionPaymentResponse>('/api/v1/subscription/buy', { plan }),
    buyFromBalance: (plan: 'week' | 'month' | 'year') =>
      http.post<{
        transaction_id: number;
        plan: string;
        subscription_expires_at: string;
        new_balance_ton: string;
      }>('/api/v1/subscription/buy-from-balance', { plan }),
  },

  paymentIntent: {
    create: (method: PaymentMethod, gateway?: CardGateway) =>
      http.post<{ id: number }>('/api/v1/payment-intent', {
        method,
        gateway: gateway ?? null,
      }),
  },

  wallet: {
    connect: (ton_wallet: string, signature?: string) =>
      http.post<{ ton_wallet: string }>('/api/v1/wallet/connect', { ton_wallet, signature }),
    disconnect: () => http.post<{ ok: boolean }>('/api/v1/wallet/disconnect'),
    withdraw: (amount_ton: number, to_address: string, request_id: string) =>
      http.post<{ transaction_id: number; status: string }>('/api/v1/wallet/withdraw', {
        amount_ton,
        to_address,
        request_id,
      }),
    deposit: (amount_ton: number) =>
      http.post<{
        transaction_id: number;
        ton_payment: { address: string; amount_nano_ton: string; comment: string; deeplink: string };
        expires_at: string;
      }>('/api/v1/wallet/deposit', { amount_ton }),
  },

  support: {
    submit: (text: string, files: File[] = []) => {
      const fd = new FormData();
      fd.append('text', text);
      files.forEach(f => fd.append('files[]', f));
      return httpForm.post<{ ticket_id: number; files_count: number }>('/api/v1/support', fd);
    },
  },
};
